import { Plus, X } from "lucide-react";
import {
  useDataProvider,
  useDelete,
  useLocaleState,
  useNotify,
  useTranslate,
} from "ra-core";
import { useQueryClient } from "@tanstack/react-query";
import {
  useEffect,
  useId,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";

import { ConfirmDeleteButton, FormActions } from "@/components/competitors/ui";
import {
  DateField,
  Field,
  FieldError,
  FormDialog,
  SELECT_CLASS,
} from "@/components/market-intel/FormDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

import { PLAN_STATUSES, planStatusLabelKey } from "./constants";
import { useMarketing } from "./context";
import {
  normalizePlanForm,
  planFormDefaults,
  type CadenceRow,
  type PhaseRow,
  type PillarRow,
  type PlanFormValues,
} from "./forms";
import { diffRows, sortPhases, sortPillars } from "./plans";
import type { ContentPlan, PlanStatus } from "./types";

const CHILD_RESOURCES = [
  "content_plan_pillars",
  "content_plan_phases",
  "content_plan_channels",
] as const;

const sameJson = (left: object, right: object, keys: string[]): boolean =>
  keys.every(
    (key) =>
      String((left as Record<string, unknown>)[key] ?? "") ===
      String((right as Record<string, unknown>)[key] ?? ""),
  );

/** Plan header + its pillars, phases and cadence per channel, in one form. */
export const PlanForm = ({
  open,
  onOpenChange,
  plan,
  onDeleted,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plan: ContentPlan | null;
  onDeleted?: () => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [locale = "es"] = useLocaleState();
  const formId = useId();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data } = useMarketing();
  const children = (id: ContentPlan["id"] | undefined) => ({
    pillars: sortPillars(
      data.pillars.filter((row) => String(row.plan_id) === String(id)),
    ),
    phases: sortPhases(
      data.phases.filter((row) => String(row.plan_id) === String(id)),
    ),
    cadences: data.cadences.filter((row) => String(row.plan_id) === String(id)),
  });
  const [values, setValues] = useState(() =>
    planFormDefaults(plan, children(plan?.id), locale),
  );
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [remove, { isPending: deleting }] = useDelete();

  useEffect(() => {
    if (open) {
      setValues(planFormDefaults(plan, children(plan?.id), locale));
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, plan]);

  const set =
    <K extends keyof PlanFormValues>(key: K) =>
    (value: PlanFormValues[K]) => {
      setValues((current) => ({ ...current, [key]: value }));
      setError(null);
    };
  const close = () => onOpenChange(false);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const result = normalizePlanForm(values);
    if (!result.ok) return setError(result.error);
    setSaving(true);
    try {
      const saved = plan
        ? (
            await dataProvider.update<ContentPlan>("content_plans", {
              id: plan.id,
              data: result.data.plan,
              previousData: plan,
            })
          ).data
        : (
            await dataProvider.create<ContentPlan>("content_plans", {
              data: result.data.plan,
            })
          ).data;
      const existing = children(plan?.id);
      const diffs = [
        diffRows(existing.pillars, result.data.pillars, (a, b) =>
          sameJson(a, b, ["code", "name", "description", "color", "position"]),
        ),
        diffRows(existing.phases, result.data.phases, (a, b) =>
          sameJson(a, b, [
            "number",
            "name",
            "start_week",
            "end_week",
            "cadence",
          ]),
        ),
        diffRows(existing.cadences, result.data.cadences, (a, b) =>
          sameJson(a, b, ["channel_id", "posts_per_week"]),
        ),
      ];
      for (const [index, diff] of diffs.entries()) {
        const resource = CHILD_RESOURCES[index];
        // Deletes first so a code / number / channel can be reused.
        if (diff.remove.length)
          await dataProvider.deleteMany(resource, { ids: diff.remove });
        for (const row of diff.update)
          await dataProvider.update(resource, {
            id: row.id,
            data: row.data,
            previousData: row.previousData,
          });
        for (const row of diff.create)
          await dataProvider.create(resource, {
            data: { ...row, plan_id: saved.id },
          });
      }
      await Promise.all(
        ["content_plans", ...CHILD_RESOURCES].map((resource) =>
          queryClient.invalidateQueries({ queryKey: [resource] }),
        ),
      );
      notify("marketing.form.saved", { type: "success" });
      close();
    } catch {
      notify("marketing.form.error", { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const id = (name: string) => `${formId}-${name}`;
  const errorId = id("error");
  const updateRow =
    <K extends "pillars" | "phases" | "cadences">(key: K, index: number) =>
    (patch: Partial<PlanFormValues[K][number]>) =>
      set(key)(
        values[key].map((row, position) =>
          position === index ? { ...row, ...patch } : row,
        ) as PlanFormValues[K],
      );
  const removeRow = (key: "pillars" | "phases" | "cadences", index: number) =>
    set(key)(values[key].filter((_, position) => position !== index) as never);

  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={translate(plan ? "marketing.plans.edit" : "marketing.plans.new")}
      formId={formId}
      onSubmit={submit}
      className="sm:max-w-2xl"
      actions={
        <>
          {plan ? (
            <ConfirmDeleteButton
              label={translate("marketing.plans.delete")}
              disabled={deleting}
              className="mr-auto"
              onConfirm={() =>
                remove(
                  "content_plans",
                  { id: plan.id, previousData: plan },
                  {
                    onSuccess: () => {
                      notify("marketing.plans.deleted", { type: "info" });
                      close();
                      onDeleted?.();
                    },
                  },
                )
              }
            />
          ) : null}
          <FormActions formId={formId} disabled={saving} onCancel={close} />
        </>
      }
    >
      <Field id={id("name")} label={translate("marketing.plans.form.name")}>
        <Input
          id={id("name")}
          value={values.name}
          onChange={(event) => set("name")(event.target.value)}
          aria-invalid={error === "marketing.form.name_required" || undefined}
          aria-describedby={error ? errorId : undefined}
          maxLength={200}
        />
      </Field>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field
          id={id("start")}
          label={translate("marketing.plans.form.start_date")}
        >
          <DateField
            id={id("start")}
            value={values.start_date}
            onChange={(event) => set("start_date")(event.target.value)}
          />
        </Field>
        <Field
          id={id("end")}
          label={translate("marketing.plans.form.end_date")}
        >
          <DateField
            id={id("end")}
            value={values.end_date}
            onChange={(event) => set("end_date")(event.target.value)}
          />
        </Field>
        <Field
          id={id("status")}
          label={translate("marketing.plan_status.label")}
        >
          <select
            id={id("status")}
            className={SELECT_CLASS}
            value={values.status}
            onChange={(event) =>
              set("status")(event.target.value as PlanStatus)
            }
          >
            {PLAN_STATUSES.map((status) => (
              <option key={status} value={status}>
                {translate(planStatusLabelKey(status))}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field
        id={id("objective")}
        label={translate("marketing.plans.form.objective")}
      >
        <Textarea
          id={id("objective")}
          rows={2}
          value={values.objective}
          onChange={(event) => set("objective")(event.target.value)}
        />
      </Field>

      <RowGroup
        legend={translate("marketing.plans.form.pillars")}
        addLabel={translate("marketing.plans.form.add_pillar")}
        onAdd={() =>
          set("pillars")([
            ...values.pillars,
            {
              code: String.fromCharCode(65 + values.pillars.length),
              name: "",
              description: "",
              color: "",
            } satisfies PillarRow,
          ])
        }
      >
        {values.pillars.map((row, index) => (
          <ChildRow
            key={row.id ?? `new-${index}`}
            onRemove={() => removeRow("pillars", index)}
          >
            <Input
              aria-label={translate("marketing.plans.form.pillar_code")}
              className="w-14 shrink-0 text-center font-semibold uppercase"
              value={row.code}
              maxLength={4}
              onChange={(event) =>
                updateRow("pillars", index)({ code: event.target.value })
              }
            />
            <div className="flex min-w-0 flex-1 flex-col gap-2">
              <Input
                aria-label={translate("marketing.plans.form.pillar_name")}
                placeholder={translate("marketing.plans.form.pillar_name")}
                value={row.name}
                onChange={(event) =>
                  updateRow("pillars", index)({ name: event.target.value })
                }
              />
              <Input
                aria-label={translate(
                  "marketing.plans.form.pillar_description",
                )}
                placeholder={translate(
                  "marketing.plans.form.pillar_description",
                )}
                value={row.description}
                onChange={(event) =>
                  updateRow(
                    "pillars",
                    index,
                  )({ description: event.target.value })
                }
              />
            </div>
          </ChildRow>
        ))}
      </RowGroup>

      <RowGroup
        legend={translate("marketing.plans.form.phases")}
        addLabel={translate("marketing.plans.form.add_phase")}
        onAdd={() =>
          set("phases")([
            ...values.phases,
            {
              number: String(values.phases.length + 1),
              name: "",
              start_week: "",
              end_week: "",
              cadence: "",
            } satisfies PhaseRow,
          ])
        }
      >
        {values.phases.map((row, index) => (
          <ChildRow
            key={row.id ?? `new-${index}`}
            onRemove={() => removeRow("phases", index)}
          >
            <div className="grid min-w-0 flex-1 grid-cols-[3.5rem_1fr] gap-2 sm:grid-cols-[3.5rem_1fr_5rem_5rem]">
              <Input
                aria-label={translate("marketing.plans.form.phase_number")}
                inputMode="numeric"
                className="text-center"
                value={row.number}
                onChange={(event) =>
                  updateRow("phases", index)({ number: event.target.value })
                }
              />
              <Input
                aria-label={translate("marketing.plans.form.phase_name")}
                placeholder={translate("marketing.plans.form.phase_name")}
                value={row.name}
                onChange={(event) =>
                  updateRow("phases", index)({ name: event.target.value })
                }
              />
              <Input
                aria-label={translate("marketing.plans.form.phase_start")}
                placeholder={translate("marketing.plans.form.phase_start")}
                inputMode="numeric"
                value={row.start_week}
                onChange={(event) =>
                  updateRow("phases", index)({ start_week: event.target.value })
                }
              />
              <Input
                aria-label={translate("marketing.plans.form.phase_end")}
                placeholder={translate("marketing.plans.form.phase_end")}
                inputMode="numeric"
                value={row.end_week}
                onChange={(event) =>
                  updateRow("phases", index)({ end_week: event.target.value })
                }
              />
              <Input
                aria-label={translate("marketing.plans.form.phase_cadence")}
                placeholder={translate("marketing.plans.form.phase_cadence")}
                className="col-span-full"
                value={row.cadence}
                onChange={(event) =>
                  updateRow("phases", index)({ cadence: event.target.value })
                }
              />
            </div>
          </ChildRow>
        ))}
      </RowGroup>

      <RowGroup
        legend={translate("marketing.plans.form.cadences")}
        addLabel={translate("marketing.plans.form.add_cadence")}
        onAdd={() =>
          set("cadences")([
            ...values.cadences,
            { channel_id: "", posts_per_week: "" } satisfies CadenceRow,
          ])
        }
      >
        {values.cadences.map((row, index) => (
          <ChildRow
            key={row.id ?? `new-${index}`}
            onRemove={() => removeRow("cadences", index)}
          >
            <select
              aria-label={translate("marketing.plans.form.cadence_channel")}
              className={SELECT_CLASS}
              value={row.channel_id}
              onChange={(event) =>
                updateRow("cadences", index)({ channel_id: event.target.value })
              }
            >
              <option value="">—</option>
              {data.channels.map((channel) => (
                <option key={channel.id} value={String(channel.id)}>
                  {channel.name}
                </option>
              ))}
            </select>
            <Input
              aria-label={translate("marketing.plans.form.cadence_per_week")}
              placeholder={translate("marketing.plans.form.cadence_per_week")}
              inputMode="decimal"
              className="w-28 shrink-0"
              value={row.posts_per_week}
              onChange={(event) =>
                updateRow(
                  "cadences",
                  index,
                )({ posts_per_week: event.target.value })
              }
            />
          </ChildRow>
        ))}
      </RowGroup>
      {error ? <FieldError id={errorId}>{translate(error)}</FieldError> : null}

      <Field id={id("notes")} label={translate("marketing.plans.form.notes")}>
        <Textarea
          id={id("notes")}
          rows={2}
          value={values.notes}
          onChange={(event) => set("notes")(event.target.value)}
        />
      </Field>
    </FormDialog>
  );
};

const RowGroup = ({
  legend,
  addLabel,
  onAdd,
  children,
}: {
  legend: string;
  addLabel: string;
  onAdd: () => void;
  children: ReactNode;
}) => (
  <fieldset className="space-y-2 rounded-2xl border border-border/70 p-3">
    <legend className="px-1 text-sm font-semibold">{legend}</legend>
    {children}
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className="h-8 rounded-full text-primary"
      onClick={onAdd}
    >
      <Plus aria-hidden className="size-4" />
      {addLabel}
    </Button>
  </fieldset>
);

const ChildRow = ({
  children,
  onRemove,
}: {
  children: ReactNode;
  onRemove: () => void;
}) => {
  const translate = useTranslate();
  return (
    <div className="flex items-start gap-2">
      {children}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-9 shrink-0 rounded-full text-muted-foreground"
        onClick={onRemove}
        aria-label={translate("marketing.plans.form.remove_row")}
      >
        <X aria-hidden className="size-4" />
      </Button>
    </div>
  );
};
